import {useState} from 'react';
import clsx from 'clsx';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {
  getHistoryFilterPeriodState,
  HISTORY_FILTER_PERIOD_NA,
  type HistoryFilterFormStateNa,
  type HistoryFilterPeriodNa,
  PERIOD_NA,
} from '@di-shared/features/history/filter/historyFilterQuery.na';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {addDate, endDateOfMonth, isSameYearAndMonth, startDateOfMonth, subDate} from '@shared/dateUtils';

export type HistoryFilterSelectBottomSheetData = HistoryFilterFormStateNa;

export default function HistoryFilterSelectBottomSheet() {
  const initial = (window as any).data as HistoryFilterSelectBottomSheetData;
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();
  const [form, setForm] = useState<HistoryFilterFormStateNa>(initial);

  function updatePeriod(period: HistoryFilterPeriodNa) {
    setForm((prev) => ({...prev, ...getHistoryFilterPeriodState(period)}));
  }

  async function openSelectMonthBottomSheet() {
    const months = Array.from({length: PERIOD_NA.SELECT_MONTH_COUNT}).map((_, index) => subDate(today, {months: index}));
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date>(ROUTE_PATHS.BOTTOM_SHEET_HISTORY_SELECT_MONTH.getFullPath(), {
      months,
      selectedDate: form.period === HISTORY_FILTER_PERIOD_NA.SELECT_MONTH ? form.startDate : undefined,
    });

    if (!date) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      period: HISTORY_FILTER_PERIOD_NA.SELECT_MONTH,
      startDate: startDateOfMonth(date),
      endDate: isSameYearAndMonth(date, today) ? today : endDateOfMonth(date),
    }));
  }

  async function openStartDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.startDate,
        minDate: form.endDate
          ? subDate(form.endDate, {days: PERIOD_NA.RECENT_90_DAYS - 1})
          : subDate(today, {days: PERIOD_NA.RECENT_90_DAYS - 1}),
        maxDate: form.endDate || today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({...prev, startDate: date}));
  }

  async function openEndDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.endDate,
        minDate: form.startDate || subDate(today, {days: PERIOD_NA.RECENT_90_DAYS - 1}),
        maxDate:
          form.startDate && form.startDate < subDate(today, {days: PERIOD_NA.RECENT_90_DAYS - 1})
            ? addDate(form.startDate, {days: PERIOD_NA.RECENT_90_DAYS - 1})
            : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({...prev, endDate: date}));
  }

  return (
    <div
      className="bottom_sheet filter_select"
      role="dialog"
      aria-modal="true"
      aria-label={t('ctn_mytrips_filter_title_eu')}
    >
      <div className="bottom_sheet_header">
        <button
          type="button"
          className="btn btn_ic"
          aria-label={t('ctn_home_close')}
          onClick={() => window.bottomSheetClose()}
        >
          <span
            className="ic ic_close_20px"
            aria-hidden="true"
          />
        </button>
      </div>
      <div className="bottom_sheet_inner">
        <div className="bottom_sheet_contents">
          <div className="setting_box">
            <h3 className="title_small_b">{t('msg_mytrips_download_select_month')}</h3>
            <button
              type="button"
              className="btn_select"
              onClick={openSelectMonthBottomSheet}
            >
              {form.period === HISTORY_FILTER_PERIOD_NA.SELECT_MONTH ? (
                <span className="select_value">{CldrDatetimeFormatter.format(form.startDate ?? today, 'yMMM')}</span>
              ) : (
                <span className="select_placeholder">{t('msg_mytrips_download_select_month')}</span>
              )}
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box">
            <h3 className="title_small_b">{t('msg_mytrips_download_select_period')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    type="button"
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === HISTORY_FILTER_PERIOD_NA.RECENT_7_DAYS})}
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD_NA.RECENT_7_DAYS)}
                  >
                    7 {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    type="button"
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === HISTORY_FILTER_PERIOD_NA.RECENT_30_DAYS})}
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD_NA.RECENT_30_DAYS)}
                  >
                    30 {t('ctn_mytrips_download_day_02')}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    type="button"
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS})}
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS)}
                  >
                    {t('ctn_mytrips_download_day_03')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    type="button"
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS,
                    })}
                    disabled={form.period !== HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS}
                    onClick={openStartDatePicker}
                  >
                    <span className="body_large_r text_date">
                      {form.startDate ? CldrDatetimeFormatter.format(form.startDate, 'medium') : t('ctn_mytrips_download_startdate')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    type="button"
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS,
                    })}
                    disabled={form.period !== HISTORY_FILTER_PERIOD_NA.CUSTOM_DAYS}
                    onClick={openEndDatePicker}
                  >
                    <span className="body_large_r text_date">
                      {form.endDate ? CldrDatetimeFormatter.format(form.endDate, 'medium') : t('ctn_mytrips_download_enddate')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bottom_sheet_footer">
          <button
            type="button"
            className="btn btn_large btn_filled"
            disabled={!form.startDate || !form.endDate}
            onClick={() => window.bottomSheetClose(form)}
          >
            {t('ctn_mytrips_filter_apply_eu')}
          </button>
        </div>
      </div>
    </div>
  );
}
